/**
 * O LANÇAMENTO DA COMISSÃO DO DIA.
 *
 * ### Por que uma gaveta no dashboard, e não uma tela própria
 *
 * Este é um gesto **diário, repetitivo e de pé**: o dono fecha o dia, olha o número na
 * Shopee e digita. Uma tela própria custaria um item de menu, uma navegação e a volta ao
 * dashboard para ver o efeito — três passos para um gesto de um. A gaveta abre do painel
 * onde o número mora, de baixo para cima no celular (seção 11 do DESIGN_SYSTEM), o campo já
 * nasce com a data de hoje e o botão de salvar ocupa a largura toda. É o caminho mais curto
 * entre "vi o valor na Shopee" e "o painel já sabe".
 *
 * ### O eco, que é a razão de ser deste arquivo
 *
 * Campo de dinheiro com erro de unidade **não quebra nada**: `123456` vira R$ 123.456,00 em
 * vez de R$ 1.234,56, o lucro por lead sai 100x maior e a tela continua bonita. Por isso o
 * campo mostra, enquanto a pessoa digita, as duas coisas que ela precisa conferir ANTES de
 * gravar: o valor interpretado (`R$ 1.234,56`) e o lucro por lead que vai sair dele. Nada de
 * `confirm()` — em gesto que se repete todo dia, confirmação vira tapinha automático e para
 * de ser lida na terceira vez.
 *
 * ### O caminho principal é um valor por MARKETPLACE (18/09)
 *
 * Data + Shopee + Amazon + Mercado Livre + salvar. Os três convivem no mesmo dia: **não é um
 * campo com seletor**, que faria o segundo lançamento sobrescrever o primeiro e mostrar a
 * última digitação no lugar da soma. Cada um é um lançamento próprio, com idempotência do
 * banco (`daily_earnings_total_do_dia_uq` inclui o marketplace).
 *
 * Campo em branco é **ausência**, não zero: quem não vende na Amazon não digita nada ali e
 * ela não aparece como R$ 0,00 no painel; quem vendeu zero digita `0`, que é uma afirmação
 * sobre o dia. Os dois estados sobrevivem à gravação e à leitura (invariante 8) — campo
 * esvaziado de um marketplace que tinha valor vira REMOÇÃO, nunca zero.
 *
 * O detalhamento por grupo fica fechado, atrás de um "opcional" — quem só tem os totais (que
 * é o normal, e vai seguir sendo) não passa por campo nenhum de grupo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Drawer, Field, Input, Skeleton, useToast } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { humanizeError } from '../../../lib/errors';
import {
  useEarningsDay,
  useEarningsRange,
  useRemoveEarnings,
  useUpsertEarnings,
} from '../../../lib/api';
import type { DayEarnings, EarningsMarketplaceName } from '../../../lib/api';
import {
  MARKETPLACE,
  ROTULO_DO_CAMPO,
  apenasDigitos,
  centavosDeDigitos,
  diaCurto,
  diaLongo,
  diasAntes,
  formatarLucroPorLead,
  mascaraDeCentavos,
  previaLucroPorLead,
  reaisDeCentavos,
} from '../../../features/earnings/earnings-ui';

/**
 * O que está digitado, por marketplace. A CHAVE é o código que o servidor manda, e a ORDEM
 * quem dita é `dia.marketplaces` — a tela não guarda uma lista própria dos marketplaces, que
 * ficaria para trás no dia em que entrasse um quarto e o campo dele nunca apareceria.
 */
type DigitosPorMarketplace = Partial<Record<EarningsMarketplaceName, string>>;

/** A chave do campo de um grupo: um valor por `(grupo, marketplace)`, como no banco. */
function chaveDoGrupo(groupId: string, marketplace: EarningsMarketplaceName): string {
  return `${groupId}:${marketplace}`;
}

const FORM_ID = 'lancamento-comissao';

/** Quantos dias a lista de conferência mostra — uma semana fecha o ciclo de quem esquece. */
const DIAS_NA_LISTA = 7;

export interface LancamentoDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** O "hoje" do workspace, **resolvido pelo servidor** no fuso dele. Nunca `new Date()`. */
  hoje: string;
}

export function LancamentoDrawer({ open, onOpenChange, hoje }: LancamentoDrawerProps) {
  const { workspace } = useWorkspace();
  const { toast } = useToast();

  const [data, setData] = useState(hoje);
  const [digitos, setDigitos] = useState<DigitosPorMarketplace>({});
  const [porGrupo, setPorGrupo] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const diaQuery = useEarningsDay(workspace.id, open ? data : null);
  const dia = diaQuery.data;

  const rangeQuery = useEarningsRange(workspace.id, diasAntes(hoje, DIAS_NA_LISTA - 1), hoje, open);

  const upsert = useUpsertEarnings();
  const remove = useRemoveEarnings();

  /**
   * Preenche o formulário UMA vez por dia escolhido — e é uma decisão, não um detalhe.
   *
   * Reescrever os campos a cada resposta do servidor apagaria o que estivesse sendo digitado
   * no instante em que uma invalidação chegasse. O `ref` guarda qual dia já foi preenchido,
   * então o refetch do MESMO dia não encosta no que está na tela.
   */
  const preenchido = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      preenchido.current = null;
      return;
    }
    if (dia === undefined || preenchido.current === dia.date) return;
    preenchido.current = dia.date;
    /*
      `null` (não lançado) vira campo VAZIO, e `0` vira "0". A distinção sobrevive à ida e à
      volta: um marketplace não lançado não pode voltar da tela como zero, senão a primeira
      edição de um dia inventaria "a Amazon vendeu R$ 0,00" para quem nunca a lançou.
    */
    setDigitos(
      Object.fromEntries(
        dia.marketplaces.map((m) => [
          m.marketplace,
          m.amountCents === null ? '' : String(m.amountCents),
        ]),
      ),
    );
    setPorGrupo(
      Object.fromEntries(
        dia.groups.flatMap((g) =>
          g.marketplaces
            .filter((m) => m.amountCents !== null)
            .map((m) => [chaveDoGrupo(g.groupId, m.marketplace), String(m.amountCents)]),
        ),
      ),
    );
  }, [open, dia]);

  // Reabrir a gaveta volta para hoje: ninguém abre o painel para lançar a terça passada.
  useEffect(() => {
    if (open) {
      setData(hoje);
      setErro(null);
      setConfirmandoExclusao(false);
    }
  }, [open, hoje]);

  const escolherDia = useCallback((novoDia: string) => {
    setData(novoDia);
    setErro(null);
    setConfirmandoExclusao(false);
    // O preenchimento do dia novo é responsabilidade do efeito acima, quando ele chegar.
    preenchido.current = null;
    setDigitos({});
    setPorGrupo({});
  }, []);

  const fatias = dia?.marketplaces ?? [];
  const centavosDe = (marketplace: EarningsMarketplaceName) =>
    centavosDeDigitos(digitos[marketplace] ?? '');

  /**
   * O que vai ser lançado, por marketplace — e o TOTAL, que é a soma dos digitados.
   *
   * `null` em um marketplace continua `null` na soma: ele não entra como zero, porque não
   * lançar não é vender zero. Total `null` (nenhum digitado) é o estado em que não há o que
   * salvar.
   */
  const digitados = fatias
    .map((m) => ({ marketplace: m.marketplace, centavos: centavosDe(m.marketplace) }))
    .filter((x): x is { marketplace: EarningsMarketplaceName; centavos: number } =>
      Number.isInteger(x.centavos),
    );
  const totalDigitado =
    digitados.length === 0 ? null : digitados.reduce((s, x) => s + x.centavos, 0);

  const participantes = dia?.membersEod ?? null;
  /**
   * A prévia é do GERAL — a soma dos três sobre a mesma base de pessoas. É o número que o
   * dono olha, e é o que o servidor vai devolver depois de gravar. A tela não divide a base
   * entre marketplaces em lugar nenhum.
   */
  const previa = totalDigitado === null ? null : previaLucroPorLead(totalDigitado, participantes);

  const submeter = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErro(null);

    if (totalDigitado === null) {
      /*
       * Campo vazio **não é zero**. Lançar R$ 0,00 é uma afirmação sobre o dia ("não vendi
       * nada naquele marketplace") e se faz digitando `0`; esvaziar o campo é não ter dito
       * nada ainda. Quem quer desfazer um lançamento usa o botão de apagar, logo abaixo.
       */
      setErro(
        'Digite o valor da comissão de pelo menos um marketplace. Para lançar zero de verdade, digite 0 — e para desfazer um lançamento, use “Apagar o lançamento deste dia”.',
      );
      return;
    }

    setSalvando(true);
    try {
      /*
       * Um lançamento POR MARKETPLACE, e só os que mudaram. Campo esvaziado de um marketplace
       * que tinha valor vira REMOÇÃO — nunca zero, que diria "vendeu nada" no lugar de
       * "ninguém informou".
       */
      for (const fatia of fatias) {
        const agora = centavosDe(fatia.marketplace);
        if (agora === fatia.amountCents) continue;
        const alvo = { workspaceId: workspace.id, date: data, marketplace: fatia.marketplace };
        if (agora === null) {
          await remove.mutateAsync({ ...alvo, groupId: null });
        } else {
          await upsert.mutateAsync({ ...alvo, groupId: null, amountCents: agora });
        }
      }

      /*
       * As fatias por grupo vão depois dos totais e só quando MUDARAM — uma por
       * `(grupo, marketplace)`, que é a granularidade do banco desde a `0021`.
       */
      for (const grupo of dia?.groups ?? []) {
        for (const fatia of grupo.marketplaces) {
          const agora = centavosDeDigitos(
            porGrupo[chaveDoGrupo(grupo.groupId, fatia.marketplace)] ?? '',
          );
          if (agora === fatia.amountCents) continue;
          const alvo = {
            workspaceId: workspace.id,
            date: data,
            groupId: grupo.groupId,
            marketplace: fatia.marketplace,
          };
          if (agora === null) {
            await remove.mutateAsync(alvo);
          } else {
            await upsert.mutateAsync({ ...alvo, amountCents: agora });
          }
        }
      }

      toast({
        title: `Comissão de ${diaLongo(data)} lançada`,
        description: `${reaisDeCentavos(totalDigitado)} somando ${digitados.length} ${
          digitados.length === 1 ? 'marketplace' : 'marketplaces'
        }.`,
        tone: 'success',
      });
      onOpenChange(false);
    } catch (error: unknown) {
      setErro(humanizeError(error));
    } finally {
      setSalvando(false);
    }
  };

  /**
   * Apagar devolve o dia inteiro para "não lançado" — **um `remove` por marketplace
   * lançado**, porque a linha do banco é por marketplace. Mandar um só apagaria a Shopee e
   * deixaria a Amazon no ar, e o painel mostraria um dia "meio apagado" que ninguém pediu.
   */
  const apagar = async () => {
    setErro(null);
    setSalvando(true);
    try {
      for (const fatia of fatias) {
        if (fatia.amountCents === null) continue;
        await remove.mutateAsync({
          workspaceId: workspace.id,
          date: data,
          groupId: null,
          marketplace: fatia.marketplace,
        });
      }
      toast({
        title: `Lançamento de ${diaLongo(data)} apagado`,
        description: 'O dia volta a ficar como “ainda não lançado”, que é diferente de R$ 0,00.',
        tone: 'success',
      });
      onOpenChange(false);
    } catch (error: unknown) {
      setErro(humanizeError(error));
    } finally {
      setSalvando(false);
      setConfirmandoExclusao(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Comissão do dia"
      /*
        A frase nomeava só a Shopee — era verdade até a `0021` e virou rótulo de fase
        anterior no minuto em que a Amazon entrou. Texto fixo que afirma um estado que a tela
        não confere é como ela mente sem quebrar.
      */
      description="Você digita o que cada marketplace mostrou no dia. O lucro por lead geral sai da SOMA deles dividida pelas pessoas que estão nos grupos monitorados — os três dividem pela mesma base."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={salvando}>
            Cancelar
          </Button>
          {/*
            O CTA da gaveta — e o único `primary` da tela inteira (seção 1.5). No celular o
            rodapé é estreito, então ele cresce para ocupar o espaço que sobra.
          */}
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            loading={salvando}
            loadingLabel="Salvando"
            className="max-md:flex-1"
          >
            Salvar lançamento
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={submeter} className="flex flex-col gap-3">
        {erro ? <FormAlert>{erro}</FormAlert> : null}

        <Field
          label="Dia"
          hint={`Vem preenchido com hoje (${diaLongo(hoje)}). Troque para lançar um dia que ficou para trás.`}
        >
          {(control) => (
            <Input
              {...control}
              type="date"
              name="date"
              max={hoje}
              value={data}
              onChange={(event) => escolherDia(event.target.value)}
            />
          )}
        </Field>

        {/*
          UM CAMPO POR MARKETPLACE, os três visíveis ao mesmo tempo. Nada de seletor: com um
          campo só, lançar a Amazon depois da Shopee seria uma TROCA, e o painel mostraria a
          última digitação achando que era a soma. A ordem é a que o servidor manda.
        */}
        {fatias.length === 0 ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          fatias.map((fatia) => (
            <Field
              key={fatia.marketplace}
              label={ROTULO_DO_CAMPO[fatia.marketplace]}
              hint={
                fatia.amountCents === null
                  ? 'Em branco quer dizer “não lançado”, que é diferente de R$ 0,00. Cada dígito é um centavo: 123456 vira R$ 1.234,56.'
                  : 'Cada dígito é um centavo: 123456 vira R$ 1.234,56. Apagar o campo desfaz o lançamento deste marketplace.'
              }
            >
              {(control) => (
                <Input
                  {...control}
                  name={`amount-${fatia.marketplace}`}
                  numeric
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0,00"
                  leadingIcon={<span className="text-xs font-medium">R$</span>}
                  value={mascaraDeCentavos(digitos[fatia.marketplace] ?? '')}
                  onChange={(event) =>
                    setDigitos((atual) => ({
                      ...atual,
                      [fatia.marketplace]: apenasDigitos(event.target.value),
                    }))
                  }
                />
              )}
            </Field>
          ))
        )}

        <EcoDoValor
          digitados={digitados}
          totalCents={totalDigitado}
          participantes={participantes}
          previaMillicents={previa}
          carregando={diaQuery.isPending}
        />

        {dia && dia.groups.length > 0 ? (
          <DetalhePorGrupo
            grupos={dia.groups}
            valores={porGrupo}
            onChange={(chave, valor) =>
              setPorGrupo((atual) => ({ ...atual, [chave]: apenasDigitos(valor) }))
            }
          />
        ) : null}
      </form>

      <UltimosDias
        dias={rangeQuery.data?.days}
        carregando={rangeQuery.isPending}
        selecionado={data}
        hoje={hoje}
        onEscolher={escolherDia}
      />

      {dia !== undefined && dia.totalCents !== null ? (
        <div className="mt-4 border-t border-border pt-3">
          {confirmandoExclusao ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Apagar devolve o dia {diaLongo(data)} para <strong>“ainda não lançado”</strong>. Não
                é o mesmo que lançar R$ 0,00 — zero diz que não houve comissão; apagado diz que
                ninguém informou.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="danger" onClick={() => void apagar()} loading={salvando}>
                  Apagar mesmo assim
                </Button>
                <Button variant="ghost" onClick={() => setConfirmandoExclusao(false)}>
                  Manter o lançamento
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmandoExclusao(true)}>
              Apagar o lançamento deste dia
            </Button>
          )}
        </div>
      ) : null}
    </Drawer>
  );
}

/* ========================================================================== */
/*  O eco — o valor interpretado e o lucro por lead ANTES de gravar            */
/* ========================================================================== */

function EcoDoValor({
  digitados,
  totalCents,
  participantes,
  previaMillicents,
  carregando,
}: {
  digitados: { marketplace: EarningsMarketplaceName; centavos: number }[];
  totalCents: number | null;
  participantes: number | null;
  previaMillicents: number | null;
  carregando: boolean;
}) {
  if (totalCents === null) {
    return (
      <p className="text-xs text-muted-foreground">
        O valor interpretado e o lucro por lead aparecem aqui assim que você começar a digitar.
      </p>
    );
  }

  return (
    /*
      `aria-live="polite"`: o eco muda a cada tecla e precisa chegar a quem não vê a tela —
      é justamente a pessoa que não tem como conferir a vírgula no olho.
    */
    <div
      role="status"
      aria-live="polite"
      aria-label="Valor interpretado"
      className="rounded-md border border-border bg-surface px-3 py-2"
    >
      {/*
        Cada marketplace digitado, e só depois a SOMA. Com um total só, um zero a mais na
        Amazon passaria despercebido dentro do número somado — e o erro de unidade é
        exatamente o que este bloco existe para pegar.
      */}
      {digitados.length > 1 ? (
        <ul className="nums mb-1 text-xs text-muted-foreground">
          {digitados.map((d) => (
            <li key={d.marketplace}>
              {MARKETPLACE[d.marketplace]}: {reaisDeCentavos(d.centavos)}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-sm">
        Vai ser lançado <strong className="nums">{reaisDeCentavos(totalCents)}</strong>
        {digitados.length > 1 ? (
          <span className="text-muted-foreground"> somando os {digitados.length} marketplaces</span>
        ) : null}
      </p>

      {carregando ? (
        <Skeleton className="mt-1 h-4 w-48" />
      ) : previaMillicents !== null && participantes !== null ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Lucro por lead GERAL com este valor:{' '}
          <strong className="nums text-foreground">{formatarLucroPorLead(previaMillicents)}</strong>{' '}
          <span className="nums">
            ({reaisDeCentavos(totalCents)} ÷ {participantes} pessoas nos grupos)
          </span>
          . Os marketplaces dividem pela <strong>mesma</strong> base de pessoas, então o geral é a
          soma dos parciais. A faixa em que isso cai quem diz é o servidor, depois de gravar.
        </p>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          Ainda não há contagem de participantes deste dia, então não dá para estimar o lucro por
          lead — e estimar zero seria inventar uma medição. O valor é lançado do mesmo jeito.
        </p>
      )}
    </div>
  );
}

/* ========================================================================== */
/*  Detalhamento por grupo — opcional, fechado, e assim deve continuar         */
/* ========================================================================== */

function DetalhePorGrupo({
  grupos,
  valores,
  onChange,
}: {
  grupos: DayEarnings['groups'];
  valores: Record<string, string>;
  /** A chave é `"<grupo>:<marketplace>"` — um valor por linha do banco (ver `chaveDoGrupo`). */
  onChange: (chave: string, valor: string) => void;
}) {
  return (
    /*
      `<details>` nativo: um toque abre, o teclado chega nele de graça e não entra biblioteca
      nova. Fechado por padrão porque a base do lucro por lead é SEMPRE o total — a quebra
      por grupo é uma conferência de quem já tem o número separado na mão.
    */
    <details className="rounded-md border border-border bg-surface">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
        Detalhar por grupo (opcional)
      </summary>
      <div className="flex flex-col gap-3 border-t border-border px-3 py-3">
        <p className="text-xs text-muted-foreground">
          Só se a Shopee te der o valor separado. Campo em branco quer dizer “este grupo não foi
          informado” — e isso não é o mesmo que R$ 0,00. O que sobrar do total aparece no painel
          como comissão não atribuída a grupo.
        </p>
        {/*
          Um campo por `(grupo, marketplace)` JÁ LANÇADO — a mesma granularidade do banco
          desde a `0021`. Só os lançados aparecem: abrir três campos vazios por grupo
          transformaria uma conferência rara em um formulário de nove campos cobrando atenção,
          e o caminho principal continua sendo o total.
        */}
        {grupos.flatMap((grupo) =>
          grupo.marketplaces
            .filter((fatia) => fatia.amountCents !== null)
            .map((fatia) => {
              const chave = chaveDoGrupo(grupo.groupId, fatia.marketplace);
              return (
                <Field
                  key={chave}
                  label={`${grupo.groupName ?? 'Grupo sem nome'} · ${MARKETPLACE[fatia.marketplace]}`}
                  hint={
                    grupo.membersEod === null
                      ? 'Sem contagem de participantes neste dia: este grupo não terá lucro por lead.'
                      : `${grupo.membersEod} pessoas no fim do dia.`
                  }
                >
                  {(control) => (
                    <Input
                      {...control}
                      numeric
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="0,00"
                      leadingIcon={<span className="text-xs font-medium">R$</span>}
                      value={mascaraDeCentavos(valores[chave] ?? '')}
                      onChange={(event) => onChange(chave, event.target.value)}
                    />
                  )}
                </Field>
              );
            }),
        )}
      </div>
    </details>
  );
}

/* ========================================================================== */
/*  Os últimos dias — para achar o que ficou sem lançar                        */
/* ========================================================================== */

function UltimosDias({
  dias,
  carregando,
  selecionado,
  hoje,
  onEscolher,
}: {
  dias: DayEarnings[] | undefined;
  carregando: boolean;
  selecionado: string;
  hoje: string;
  onEscolher: (dia: string) => void;
}) {
  return (
    <section className="mt-4 border-t border-border pt-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Últimos dias
      </h3>

      {carregando ? (
        <Skeleton className="mt-2 h-24 w-full" />
      ) : dias === undefined || dias.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">Nenhum dia para conferir ainda.</p>
      ) : (
        <ul className="mt-2 flex flex-col">
          {[...dias].reverse().map((d) => {
            const ativo = d.date === selecionado;
            return (
              <li key={d.date}>
                <button
                  type="button"
                  onClick={() => onEscolher(d.date)}
                  aria-current={ativo ? 'true' : undefined}
                  aria-label={`Lançar a comissão de ${diaLongo(d.date)}`}
                  className={
                    ativo
                      ? 'flex min-h-11 w-full items-center justify-between gap-3 rounded-md border border-border-strong px-2 text-left text-sm'
                      : 'flex min-h-11 w-full items-center justify-between gap-3 rounded-md border border-transparent px-2 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  }
                >
                  <span className="nums shrink-0 text-muted-foreground">
                    {diaCurto(d.date)}
                    {d.date === hoje ? ' · hoje' : ''}
                  </span>
                  {/*
                    A distinção que vale a lista inteira: dia sem lançamento é "não lançado",
                    dia com zero é "R$ 0,00". Mostrar zero nos dois transformaria o
                    esquecimento em fato consumado — e ninguém voltaria para consertar.
                  */}
                  {d.totalCents === null ? (
                    <span className="text-muted-foreground">não lançado</span>
                  ) : (
                    <span className="nums">
                      {reaisDeCentavos(d.totalCents)}
                      {d.profitPerLeadMillicents !== null
                        ? ` · ${formatarLucroPorLead(d.profitPerLeadMillicents)}/lead`
                        : ''}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
